import React from "react";
import styles from "./GamePadMode.module.css";

// Disposition « standard » du navigateur (Gamepad API), valable pour DualShock 4, DualSense et manettes Xbox.
export const BTN = {
    CROSS: 0, CIRCLE: 1, SQUARE: 2, TRIANGLE: 3,
    L1: 4, R1: 5, L2: 6, R2: 7,
    SHARE: 8, OPTIONS: 9, L3: 10, R3: 11,
    UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15,
    PS: 16, PAD: 17,
};

const cx = (...names) => names.filter(Boolean).join(" ");

/**
 * Manette dessinée en SVG qui suit en temps réel l'état lu sur la vraie manette.
 *
 * @param snap   {buttons: number[], axes: number[]}  état de la manette (valeurs de 0 à 1, axes de -1 à 1)
 * @param tested {[clé]: true}                         touches déjà essayées (contour vert)
 * @param sony   true : symboles PlayStation, false : lettres Xbox
 */
export default function ControllerLive({snap, tested, sony}) {
    const val = i => snap.buttons[i] ?? 0;
    const on = (i, seuil = 0.5) => val(i) > seuil;
    const part = (key, actif) => cx(styles.part, actif && styles.active, tested[key] && styles.tested);

    const lx = snap.axes[0] ?? 0, ly = snap.axes[1] ?? 0;
    const rx = snap.axes[2] ?? 0, ry = snap.axes[3] ?? 0;
    const lMove = Math.hypot(lx, ly) > 0.15, rMove = Math.hypot(rx, ry) > 0.15;

    const face = (key, i, x, y, couleur, glyphe) => (
        <g key={key} transform={`translate(${x} ${y})`} style={{"--c": couleur}}
           className={cx(styles.face, on(i) && styles.active, tested[key] && styles.tested)}>
            <circle r="21" className={styles.faceBase}/>
            {glyphe}
        </g>
    );

    const symbole = {
        triangle: <path d="M0 -10 L10 7 L-10 7 Z" className={styles.glyph}/>,
        circle: <circle r="9" className={styles.glyph}/>,
        cross: <path d="M-8 -8 L8 8 M8 -8 L-8 8" className={styles.glyph}/>,
        square: <rect x="-8" y="-8" width="16" height="16" className={styles.glyph}/>,
    };
    const lettre = t => <text className={styles.letter} textAnchor="middle" dy="7">{t}</text>;

    const stick = (key, x, y, ax, ay, bouton, bouge) => (
        <g transform={`translate(${x} ${y})`}>
            <circle r="42" className={styles.stickWell}/>
            <g transform={`translate(${ax * 15} ${ay * 15})`}
               className={cx(styles.stick, (on(bouton) || bouge) && styles.active, tested[key] && styles.tested,
                   on(bouton) && styles.pressed)}>
                <circle r="30" className={styles.stickCap}/>
                <circle r="17" className={styles.stickDimple}/>
            </g>
        </g>
    );

    const gachette = (key, i, x) => {
        const v = val(i), h = 50;
        return (
            <g className={cx(styles.trigger, tested[key] && styles.tested)}>
                <rect x={x} y="34" width="100" height={h} rx="14" className={styles.triggerBase}/>
                <clipPath id={"clip-" + key}><rect x={x} y={34 + h * (1 - v)} width="100" height={h * v + 1}/></clipPath>
                <rect x={x} y="34" width="100" height={h} rx="14" className={styles.triggerFill}
                      clipPath={`url(#clip-${key})`}/>
                <text x={x + 50} y="66" textAnchor="middle" className={styles.tag}>{key.toUpperCase()}</text>
            </g>
        );
    };

    const epaule = (key, i, x) => (
        <g className={part(key, on(i))}>
            <rect x={x} y="92" width="130" height="24" rx="12" className={styles.shape}/>
            <text x={x + 65} y="109" textAnchor="middle" className={styles.tag}>{key.toUpperCase()}</text>
        </g>
    );

    return (
        <svg viewBox="0 0 800 520" className={styles.pad} role="img" aria-label="Manette en fonctionnement">
            {/* ombre et corps */}
            <ellipse cx="400" cy="492" rx="300" ry="16" className={styles.shadow}/>
            <path className={styles.body}
                  d="M250 120 C200 110 150 120 120 160 C80 215 40 360 50 430 C58 480 110 495 150 460 C190 425 215 370 260 345 L540 345 C585 370 610 425 650 460 C690 495 742 480 750 430 C760 360 720 215 680 160 C650 120 600 110 550 120 C500 128 480 135 400 135 C320 135 300 128 250 120 Z"/>

            {gachette("l2", BTN.L2, 160)}
            {gachette("r2", BTN.R2, 540)}
            {epaule("l1", BTN.L1, 140)}
            {epaule("r1", BTN.R1, 530)}

            {/* pavé tactile (DualShock / DualSense) */}
            {sony && snap.buttons.length > BTN.PAD && (
                <g className={part("pad", on(BTN.PAD))}>
                    <rect x="300" y="150" width="200" height="104" rx="18" className={styles.shape}/>
                    <rect x="372" y="138" width="56" height="6" rx="3" className={styles.lightbar}/>
                </g>
            )}

            {/* croix directionnelle */}
            <g transform="translate(190 215)">
                <path className={styles.dpadBase}
                      d="M-16 -50 H16 V-16 H50 V16 H16 V50 H-16 V16 H-50 V-16 H-16 Z"/>
                <path className={part("up", on(BTN.UP))} d="M-16 -50 H16 V-16 H-16 Z"/>
                <path className={part("down", on(BTN.DOWN))} d="M-16 16 H16 V50 H-16 Z"/>
                <path className={part("left", on(BTN.LEFT))} d="M-50 -16 H-16 V16 H-50 Z"/>
                <path className={part("right", on(BTN.RIGHT))} d="M16 -16 H50 V16 H16 Z"/>
            </g>

            {/* boutons d'action */}
            {sony ? <>
                {face("triangle", BTN.TRIANGLE, 610, 168, "#34d399", symbole.triangle)}
                {face("circle", BTN.CIRCLE, 658, 215, "#f87171", symbole.circle)}
                {face("cross", BTN.CROSS, 610, 262, "#60a5fa", symbole.cross)}
                {face("square", BTN.SQUARE, 562, 215, "#f472b6", symbole.square)}
            </> : <>
                {face("triangle", BTN.TRIANGLE, 610, 168, "#facc15", lettre("Y"))}
                {face("circle", BTN.CIRCLE, 658, 215, "#f87171", lettre("B"))}
                {face("cross", BTN.CROSS, 610, 262, "#4ade80", lettre("A"))}
                {face("square", BTN.SQUARE, 562, 215, "#60a5fa", lettre("X"))}
            </>}

            {/* Share / Options */}
            <g className={part("share", on(BTN.SHARE))} transform="translate(262 176) rotate(-14)">
                <rect x="-5" y="-14" width="10" height="28" rx="5" className={styles.shape}/>
            </g>
            <g className={part("options", on(BTN.OPTIONS))} transform="translate(538 176) rotate(14)">
                <rect x="-5" y="-14" width="10" height="28" rx="5" className={styles.shape}/>
            </g>

            {/* bouton PS */}
            {snap.buttons.length > BTN.PS && (
                <g className={part("ps", on(BTN.PS))}>
                    <circle cx="400" cy="300" r="17" className={styles.shape}/>
                    <text x="400" y="306" textAnchor="middle" className={styles.tag}>{sony ? "PS" : "◎"}</text>
                </g>
            )}

            {stick("ls", 292, 300, lx, ly, BTN.L3, lMove)}
            {stick("rs", 508, 300, rx, ry, BTN.R3, rMove)}
        </svg>
    );
}
